(() => {
  if (customElements.get('collection-bundle-progress')) return;

  const instances = new Set();
  let unsubscribe, cartObserver, requestController, scheduled = false, revision = 0;

  function scheduleRefresh() {
    revision += 1;
    requestController?.abort();
    if (scheduled || !instances.size) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      if (instances.size) refreshCount();
    });
  }

  async function refreshCount() {
    const instance = instances.values().next().value;
    const currentRevision = revision;
    const controller = new AbortController();
    requestController = controller;
    const url = new URL(window.location.href);
    url.searchParams.delete('sections');
    url.searchParams.set('section_id', instance.dataset.sectionId);
    try {
      const response = await fetch(url, {
        credentials: 'same-origin', cache: 'no-store', signal: controller.signal
      });
      if (!response.ok) throw new Error('Bundle progress unavailable');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const element = doc.querySelector('collection-bundle-progress[data-count]');
      const count = element ? Number(element.dataset.count) : NaN;
      if (!Number.isInteger(count) || count < 0) throw new Error('Invalid bundle quantity');
      if (currentRevision !== revision || controller.signal.aborted) return;
      instances.forEach(component => component.updateCount(count));
    } catch (error) {
      if (error.name === 'AbortError' || currentRevision !== revision) return;
      instances.forEach(component => {
        component.querySelector('[data-bundle-error]').textContent = 'Bundle progress could not be updated. Refresh the page to see your latest quantity.';
      });
    } finally {
      if (requestController === controller) requestController = null;
    }
  }

  function handlePageShow(event) {
    if (event.persisted) scheduleRefresh();
  }

  function start() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleRefresh);
    }
    const cartBody = document.querySelector('#CartDrawer-Body');
    if (cartBody) {
      cartObserver = new MutationObserver(scheduleRefresh);
      cartObserver.observe(cartBody, { childList: true, subtree: true });
    }
    window.addEventListener('pageshow', handlePageShow);
  }

  function stop() {
    unsubscribe?.();
    unsubscribe = null;
    cartObserver?.disconnect();
    cartObserver = null;
    requestController?.abort();
    revision += 1;
    window.removeEventListener('pageshow', handlePageShow);
  }

  class CollectionBundleProgress extends HTMLElement {
    connectedCallback() {
      if (instances.has(this)) return;
      const reconnecting = this.initialized;
      this.details = this.querySelector('details');
      if (!this.details) return;
      this.summary = this.details.querySelector('summary');
      this.targetExpanded = this.details.open;
      this.handleSummaryClick = event => {
        event.preventDefault();
        this.setExpanded(!this.targetExpanded);
      };
      this.summary?.addEventListener('click', this.handleSummaryClick);
      this.tiers = Array.from(this.querySelectorAll('[data-bundle-tier]'));
      this.reserveSpace = () => {
        this.closest('.bundle-progress-section')?.style.setProperty('--bundle-progress-space', `${this.offsetHeight + 32}px`);
      };
      this.resizeObserver = new ResizeObserver(this.reserveSpace);
      this.resizeObserver.observe(this);
      this.editorRoot = this.closest('.shopify-section');
      this.handleBlockSelect = () => this.setExpanded(true);
      this.editorRoot?.addEventListener('shopify:block:select', this.handleBlockSelect);
      instances.add(this);
      if (instances.size === 1) start();
      this.updateCount(Number(this.dataset.count));
      this.initialized = true;
      if (reconnecting || document.readyState === 'complete') scheduleRefresh();
    }

    disconnectedCallback() {
      this.summary?.removeEventListener('click', this.handleSummaryClick);
      this.finishToggle();
      this.resetUpdateEffect();
      this.resizeObserver?.disconnect();
      this.editorRoot?.removeEventListener('shopify:block:select', this.handleBlockSelect);
      instances.delete(this);
      if (!instances.size) stop();
    }

    finishToggle() {
      this.toggleAnimation?.cancel();
      this.toggleAnimation = null;
      if (!this.details) return;
      if (typeof this.targetExpanded === 'boolean') this.details.open = this.targetExpanded;
      this.details.classList.remove('is-toggling', 'is-closing');
    }

    setExpanded(expanded) {
      const startHeight = this.details.getBoundingClientRect().height;
      this.finishToggle();
      this.targetExpanded = expanded;
      this.details.open = expanded;
      const endHeight = this.details.getBoundingClientRect().height;
      if (!this.isConnected || Math.abs(startHeight - endHeight) < 1 ||
          window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      // Keep the native content rendered until the closing animation finishes.
      this.details.open = true;
      this.details.classList.add('is-toggling');
      this.details.classList.toggle('is-closing', !expanded);
      const animation = this.details.animate([
        { height: `${startHeight}px` },
        { height: `${endHeight}px` }
      ], { duration: 300, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'both' });
      this.toggleAnimation = animation;
      animation.onfinish = () => {
        if (this.toggleAnimation === animation) this.finishToggle();
      };
    }

    updateCount(count) {
      const maximum = Number(this.dataset.maximum);
      if (!Number.isInteger(count) || count < 0 || maximum <= 0) return;
      const previousCount = Number(this.dataset.count);
      this.dataset.count = String(count);
      this.style.setProperty('--bundle-progress-fill', `${Math.min(count / maximum, 1) * 100}%`);
      let earnedDiscount = 0;
      this.tiers.forEach(tier => {
        const reached = count >= Number(tier.dataset.bundleTier);
        tier.classList.toggle('is-reached', reached);
        if (reached) earnedDiscount = Number(tier.dataset.discount);
      });
      const track = this.querySelector('[data-bundle-track]');
      track.setAttribute('aria-valuenow', String(Math.min(count, maximum)));
      track.setAttribute('aria-valuetext', `${count} press on ${count === 1 ? 'design' : 'designs'}; ${earnedDiscount}% discount tier reached`);
      const next = this.tiers.find(tier => Number(tier.dataset.bundleTier) > count);
      const remaining = next ? Number(next.dataset.bundleTier) - count : 0;
      const message = this.querySelector('[data-bundle-message]');
      if (next) {
        const discount = document.createElement('strong');
        discount.className = 'collection-bundle-progress__discount';
        discount.textContent = `${next.dataset.discount}% off`;
        message.replaceChildren(`Add ${remaining} more press on ${remaining === 1 ? 'design' : 'designs'} for `, discount);
      } else {
        message.textContent = this.dataset.completionMessage || '';
      }
      this.querySelector('[data-bundle-error]').textContent = '';
      if (this.initialized && count !== previousCount) this.showUpdateEffect(count, previousCount);
    }

    resetUpdateEffect() {
      clearTimeout(this.updateEffectTimer);
      this.updateAnimations?.forEach(animation => animation.cancel());
      this.updateAnimations = [];
      this.classList.remove('is-updated');
      this.tiers?.forEach(tier => tier.classList.remove('is-newly-reached'));
      const status = this.querySelector('[data-bundle-update]');
      if (status) status.textContent = '';
    }

    showUpdateEffect(count, previousCount) {
      if (!this.isConnected) return;
      this.resetUpdateEffect();
      const added = count - previousCount;
      const newTiers = this.tiers.filter(tier =>
        previousCount < Number(tier.dataset.bundleTier) && count >= Number(tier.dataset.bundleTier)
      );
      const latestTier = newTiers[newTiers.length - 1];
      const status = this.querySelector('[data-bundle-update]');
      if (status) {
        status.textContent = latestTier
          ? `✓ +${added} added · ${latestTier.dataset.discount}% tier reached`
          : added > 0
            ? `✓ ${added} ${added === 1 ? 'design' : 'designs'} added to your bundle`
            : `Bundle updated · ${count} ${count === 1 ? 'design' : 'designs'}`;
      }
      this.classList.add('is-updated');
      newTiers.forEach(tier => tier.classList.add('is-newly-reached'));
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.updateAnimations.push(this.animate([
          { transform: 'translateY(0)' },
          { transform: 'translateY(-5px)', offset: 0.35 },
          { transform: 'translateY(0)' }
        ], { duration: 650, easing: 'ease-out' }));
        const shine = this.querySelector('.collection-bundle-progress__shine');
        if (shine) this.updateAnimations.push(shine.animate([
          { transform: 'translateX(-100%)', opacity: 0 },
          { opacity: 1, offset: 0.2 },
          { transform: 'translateX(100%)', opacity: 0 }
        ], { duration: 1000, easing: 'ease-in-out' }));
        const targets = newTiers.map(tier => tier.querySelector('.collection-bundle-progress__badge'));
        targets.filter(Boolean).forEach(target => {
          this.updateAnimations.push(target.animate([
            { transform: 'scale(1)' },
            { transform: 'scale(1.16)', offset: 0.4 },
            { transform: 'scale(1)' }
          ], { duration: 550, easing: 'ease-out' }));
        });
      }
      this.updateEffectTimer = setTimeout(() => this.resetUpdateEffect(), 3000);
    }
  }

  customElements.define('collection-bundle-progress', CollectionBundleProgress);
})();
